export const SECTION_NAME_MAX_LENGTH = 255;

export const UNSECTIONED_SECTION_KEY = 'unsectioned';

export interface DeliveryMethodSection {
  id: string;
  name: string;
}

export interface OrderableDeliveryMethod {
  id?: string;
  deliveryMethodSectionId?: string | null;
  manualSortOrder?: number | null;
}

export interface DeliveryMethodDisplayOrder {
  sections: DeliveryMethodSection[];
  methodIdsBySectionKey: Record<string, string[]>;
}

export interface DeliveryMethodDisplayOrderPayload {
  deliveryMethodSections: DeliveryMethodSection[];
  deliveryMethodPlacements: { id: number; deliveryMethodSectionId: string | null; manualSortOrder: number }[];
}

export const sortDeliveryMethodsByManualOrder = <Method extends OrderableDeliveryMethod>(methods: Method[]): Method[] =>
  [...methods].sort((firstMethod, secondMethod) => {
    const firstHasManualOrder = firstMethod.manualSortOrder != null;
    const secondHasManualOrder = secondMethod.manualSortOrder != null;
    if (firstHasManualOrder !== secondHasManualOrder) {
      return firstHasManualOrder ? 1 : -1;
    }
    if (firstHasManualOrder && firstMethod.manualSortOrder !== secondMethod.manualSortOrder) {
      return firstMethod.manualSortOrder - secondMethod.manualSortOrder;
    }
    return Number(secondMethod.id) - Number(firstMethod.id);
  });

export const buildDeliveryMethodDisplayOrder = (
  methods: OrderableDeliveryMethod[],
  sections: DeliveryMethodSection[],
): DeliveryMethodDisplayOrder => {
  const methodIdsBySectionKey: Record<string, string[]> = { [UNSECTIONED_SECTION_KEY]: [] };
  sections.forEach((section) => (methodIdsBySectionKey[section.id] = []));
  sortDeliveryMethodsByManualOrder(methods).forEach((method) => {
    const sectionKey =
      method.deliveryMethodSectionId && methodIdsBySectionKey[method.deliveryMethodSectionId]
        ? method.deliveryMethodSectionId
        : UNSECTIONED_SECTION_KEY;
    methodIdsBySectionKey[sectionKey].push(String(method.id));
  });
  return { sections, methodIdsBySectionKey };
};

export const reconcileDisplayOrderWithExistingMethods = (
  displayOrder: DeliveryMethodDisplayOrder,
  existingMethodIds: string[],
): DeliveryMethodDisplayOrder => {
  const existingMethodIdSet = new Set(existingMethodIds);
  const placedMethodIds = new Set<string>();
  const methodIdsBySectionKey: Record<string, string[]> = {};
  Object.entries(displayOrder.methodIdsBySectionKey).forEach(([sectionKey, methodIds]) => {
    methodIdsBySectionKey[sectionKey] = methodIds.filter((methodId) => existingMethodIdSet.has(methodId));
    methodIdsBySectionKey[sectionKey].forEach((methodId) => placedMethodIds.add(methodId));
  });
  const unplacedMethodIds = existingMethodIds.filter((methodId) => !placedMethodIds.has(methodId));
  methodIdsBySectionKey[UNSECTIONED_SECTION_KEY] = [
    ...unplacedMethodIds,
    ...(methodIdsBySectionKey[UNSECTIONED_SECTION_KEY] ?? []),
  ];
  return { sections: displayOrder.sections, methodIdsBySectionKey };
};

export const getMethodIdsInDisplayOrder = (displayOrder: DeliveryMethodDisplayOrder): string[] => [
  ...displayOrder.sections.flatMap((section) => displayOrder.methodIdsBySectionKey[section.id] ?? []),
  ...(displayOrder.methodIdsBySectionKey[UNSECTIONED_SECTION_KEY] ?? []),
];

/**
 * `destinationIndex` is the index the drag library reports, which counts positions in the target section with only
 * the dragged row removed. The other moving rows are removed too, so the index is shifted back by how many of them
 * sat before it.
 */
export const moveDeliveryMethods = (
  displayOrder: DeliveryMethodDisplayOrder,
  draggedMethodId: string,
  movingMethodIds: string[],
  targetSectionKey: string,
  destinationIndex: number,
): DeliveryMethodDisplayOrder => {
  const movingMethodIdSet = new Set([...movingMethodIds, draggedMethodId]);
  const movingMethodIdsInDisplayOrder = getMethodIdsInDisplayOrder(displayOrder).filter((methodId) =>
    movingMethodIdSet.has(methodId),
  );
  const targetSectionWithoutDraggedMethod = (displayOrder.methodIdsBySectionKey[targetSectionKey] ?? []).filter(
    (methodId) => methodId !== draggedMethodId,
  );
  const movingMethodsBeforeDestination = targetSectionWithoutDraggedMethod
    .slice(0, destinationIndex)
    .filter((methodId) => movingMethodIdSet.has(methodId)).length;
  const insertionIndex = destinationIndex - movingMethodsBeforeDestination;

  const methodIdsBySectionKey: Record<string, string[]> = {};
  Object.entries(displayOrder.methodIdsBySectionKey).forEach(([sectionKey, methodIds]) => {
    methodIdsBySectionKey[sectionKey] = methodIds.filter((methodId) => !movingMethodIdSet.has(methodId));
  });
  const targetSectionMethodIds = methodIdsBySectionKey[targetSectionKey] ?? [];
  methodIdsBySectionKey[targetSectionKey] = [
    ...targetSectionMethodIds.slice(0, insertionIndex),
    ...movingMethodIdsInDisplayOrder,
    ...targetSectionMethodIds.slice(insertionIndex),
  ];
  return { sections: displayOrder.sections, methodIdsBySectionKey };
};

export const convertVisibleDestinationIndexToSectionIndex = (
  sectionMethodIds: string[],
  visibleSectionMethodIds: string[],
  draggedMethodId: string,
  visibleDestinationIndex: number,
): number => {
  const sectionWithoutDraggedMethod = sectionMethodIds.filter((methodId) => methodId !== draggedMethodId);
  const visibleWithoutDraggedMethod = visibleSectionMethodIds.filter((methodId) => methodId !== draggedMethodId);
  const visibleMethodBeforeDestination = visibleWithoutDraggedMethod[visibleDestinationIndex - 1];
  if (visibleMethodBeforeDestination) {
    return sectionWithoutDraggedMethod.indexOf(visibleMethodBeforeDestination) + 1;
  }
  const visibleMethodAtDestination = visibleWithoutDraggedMethod[visibleDestinationIndex];
  return visibleMethodAtDestination ? sectionWithoutDraggedMethod.indexOf(visibleMethodAtDestination) : 0;
};

export const moveDeliveryMethodsToEndOfSection = (
  displayOrder: DeliveryMethodDisplayOrder,
  movingMethodIds: string[],
  targetSectionKey: string,
): DeliveryMethodDisplayOrder =>
  moveDeliveryMethods(displayOrder, movingMethodIds[0], movingMethodIds, targetSectionKey, Number.MAX_SAFE_INTEGER);

export const reorderSections = (
  displayOrder: DeliveryMethodDisplayOrder,
  sourceIndex: number,
  destinationIndex: number,
): DeliveryMethodDisplayOrder => {
  const sections = [...displayOrder.sections];
  const [movedSection] = sections.splice(sourceIndex, 1);
  sections.splice(destinationIndex, 0, movedSection);
  return { ...displayOrder, sections };
};

export const addSection = (
  displayOrder: DeliveryMethodDisplayOrder,
  section: DeliveryMethodSection,
): DeliveryMethodDisplayOrder => ({
  sections: [section, ...displayOrder.sections],
  methodIdsBySectionKey: { ...displayOrder.methodIdsBySectionKey, [section.id]: [] },
});

export const renameSection = (
  displayOrder: DeliveryMethodDisplayOrder,
  sectionId: string,
  name: string,
): DeliveryMethodDisplayOrder => ({
  ...displayOrder,
  sections: displayOrder.sections.map((section) => (section.id === sectionId ? { ...section, name } : section)),
});

export const deleteSection = (
  displayOrder: DeliveryMethodDisplayOrder,
  sectionId: string,
): DeliveryMethodDisplayOrder => {
  const { [sectionId]: methodIdsOfDeletedSection = [], ...remainingSections } = displayOrder.methodIdsBySectionKey;
  return {
    sections: displayOrder.sections.filter((section) => section.id !== sectionId),
    methodIdsBySectionKey: {
      ...remainingSections,
      [UNSECTIONED_SECTION_KEY]: [...(remainingSections[UNSECTIONED_SECTION_KEY] ?? []), ...methodIdsOfDeletedSection],
    },
  };
};

export const toDisplayOrderPayload = (displayOrder: DeliveryMethodDisplayOrder): DeliveryMethodDisplayOrderPayload => {
  const sectionIdByMethodId = new Map<string, string | null>();
  displayOrder.sections.forEach((section) =>
    (displayOrder.methodIdsBySectionKey[section.id] ?? []).forEach((methodId) =>
      sectionIdByMethodId.set(methodId, section.id),
    ),
  );
  return {
    deliveryMethodSections: displayOrder.sections,
    deliveryMethodPlacements: getMethodIdsInDisplayOrder(displayOrder).map((methodId, manualSortOrder) => ({
      id: Number(methodId),
      deliveryMethodSectionId: sectionIdByMethodId.get(methodId) ?? null,
      manualSortOrder,
    })),
  };
};

export const createDeliveryMethodSectionId = (): string =>
  `section-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export const keepDraftEditedDuringSave = (
  currentDraft: DeliveryMethodDisplayOrder | null,
  draftBeingSaved: DeliveryMethodDisplayOrder | null,
): DeliveryMethodDisplayOrder | null => (currentDraft === draftBeingSaved ? null : currentDraft);
